import React, { useState, useEffect } from 'react';
import {
  Landmark, Plus, Search, Edit2, Trash2, Phone, Building,
  CheckCircle2, Users, TrendingUp, ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { pejabatPublikApi } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';

const DAPIL_OPTIONS = [
  'Dapil 1',
  'Dapil 2',
  'Dapil 3',
  'Dapil 4',
  'Dapil 5',
  'Dapil 6',
];

const MEDSOS_COLOR = {
  Atas: 'text-emerald-700 font-bold',
  Tengah: 'text-amber-700 font-bold',
  Bawah: 'text-gray-600 font-medium',
};

const normalizeWa = (hp) => {
  if (!hp) return '';
  let s = String(hp).replace(/\D/g, '');
  if (s.startsWith('0')) s = '62' + s.slice(1);
  else if (!s.startsWith('62')) s = '62' + s;
  return s;
};

const PejabatPublik = () => {
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'admin_pusat', 'admin_input'].includes(user?.role);

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedDapil, setSelectedDapil] = useState('all');
  const [search, setSearch] = useState('');

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    nama: '',
    jabatan: '',
    lembaga: 'DPRD Kab. Sukabumi',
    dapil: 'Dapil 1',
    suara_pribadi_2024: '',
    suara_partai: '',
    relawan: '',
    target_relawan: '',
    target_suara_2029: '',
    keterangan_target: 'Target 2029',
    skor_medsos: '',
    progres_sisir_rw: '[ISI DATA]',
    hp: '',
    keterangan: '',
  });

  // Delete State
  const [deleteId, setDeleteId] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [listRes, summaryRes] = await Promise.all([
        pejabatPublikApi.list({
          search: search || undefined,
        }),
        pejabatPublikApi.summary(),
      ]);
      setItems(listRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      toast({ title: 'Gagal memuat data pejabat publik', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadData();
  };

  const handleOpenAdd = () => {
    setEditingItem(null);
    setForm({
      nama: '',
      jabatan: '',
      lembaga: 'DPRD Kab. Sukabumi',
      dapil: selectedDapil !== 'all' ? selectedDapil : 'Dapil 1',
      suara_pribadi_2024: '',
      suara_partai: '—',
      relawan: '',
      target_relawan: '—',
      target_suara_2029: '',
      keterangan_target: 'Target 2029',
      skor_medsos: '60,0 · Tengah',
      progres_sisir_rw: '[ISI DATA]',
      hp: '',
      keterangan: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setForm({
      nama: item.nama || '',
      jabatan: item.jabatan || '',
      lembaga: item.lembaga || 'DPRD Kab. Sukabumi',
      dapil: item.dapil || 'Dapil 1',
      suara_pribadi_2024: item.suara_pribadi_2024 ?? '',
      suara_partai: item.suara_partai || '—',
      relawan: item.relawan || '',
      target_relawan: item.target_relawan || '—',
      target_suara_2029: item.target_suara_2029 ?? '',
      keterangan_target: item.keterangan_target || '',
      skor_medsos: item.skor_medsos || '',
      progres_sisir_rw: item.progres_sisir_rw || '[ISI DATA]',
      hp: item.hp || '',
      keterangan: item.keterangan || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nama.trim() || !form.jabatan.trim()) {
      toast({ title: 'Nama dan jabatan wajib diisi', variant: 'destructive' });
      return;
    }
    const payload = {
      ...form,
      suara_pribadi_2024: form.suara_pribadi_2024 ? parseInt(form.suara_pribadi_2024, 10) : 0,
      target_suara_2029: form.target_suara_2029 ? parseInt(form.target_suara_2029, 10) : 0,
    };

    try {
      if (editingItem) {
        await pejabatPublikApi.update(editingItem.id, payload);
        toast({ title: 'Data Pejabat Publik berhasil diperbarui' });
      } else {
        await pejabatPublikApi.create(payload);
        toast({ title: 'Pejabat Publik berhasil ditambahkan' });
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Gagal menyimpan data', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await pejabatPublikApi.remove(deleteId);
      toast({ title: 'Data pejabat publik berhasil dihapus' });
      setDeleteId(null);
      loadData();
    } catch (err) {
      toast({ title: 'Gagal menghapus data', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const filteredItems = items.filter((row) => {
    if (selectedDapil !== 'all' && row.dapil !== selectedDapil) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (row.nama || '').toLowerCase().includes(q) ||
        (row.jabatan || '').toLowerCase().includes(q) ||
        (row.dapil || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* PRESENTATION HEADER BANNER */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-xs relative overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-8 h-1.5 bg-orange-500 rounded-full inline-block"></span>
            <span className="text-xs font-black tracking-widest text-orange-600 uppercase">
              PEJABAT PUBLIK
            </span>
          </div>
          <span className="text-[11px] font-bold text-gray-400 tracking-wider uppercase">
            DPD KABUPATEN SUKABUMI
          </span>
        </div>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">
              Kekuatan Pejabat Publik PKS Kab. Sukabumi
            </h1>
            <p className="text-xs text-gray-500 font-medium mt-1">
              Pemetaan basis suara, kekuatan relawan, target 2029, dan skor pengaruh media sosial anggota dewan terpilih.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                onClick={handleOpenAdd}
                className="h-10 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-xs gap-1.5 shadow-sm shadow-orange-500/20"
              >
                <Plus className="w-4 h-4" />
                Tambah Pejabat Publik
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* FILTER & TOOLBAR */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        {/* DAPIL TABS */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
          <button
            onClick={() => setSelectedDapil('all')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex-shrink-0 ${
              selectedDapil === 'all'
                ? 'bg-orange-500 text-white shadow-sm shadow-orange-500/20'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            Semua Dapil ({items.length})
          </button>
          {DAPIL_OPTIONS.map((d) => {
            const count = items.filter((it) => it.dapil === d).length;
            return (
              <button
                key={d}
                onClick={() => setSelectedDapil(d)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex-shrink-0 ${
                  selectedDapil === d
                    ? 'bg-orange-500 text-white shadow-sm shadow-orange-500/20'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {d} {count > 0 && `(${count})`}
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSearchSubmit} className="flex items-center gap-2 max-w-xs">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari pejabat / dapil..."
              className="pl-8 h-9 rounded-xl font-medium text-xs bg-white"
            />
          </div>
          <Button type="submit" variant="secondary" className="h-9 rounded-xl px-3 text-xs font-bold">
            Cari
          </Button>
        </form>
      </div>

      {/* EXACT SLIDE PRESENTATION TABLE */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-gray-900 text-white">
              <TableRow className="hover:bg-gray-900 border-none">
                <TableHead className="text-white font-extrabold text-xs py-3.5 px-4 min-w-[200px]">
                  Pejabat publik (DPRD Kab)
                </TableHead>
                <TableHead className="text-white font-extrabold text-xs py-3.5 px-3 whitespace-nowrap">
                  Dapil
                </TableHead>
                <TableHead className="text-white font-extrabold text-xs py-3.5 px-3 text-right whitespace-nowrap">
                  Suara pribadi 2024
                </TableHead>
                <TableHead className="text-white font-extrabold text-xs py-3.5 px-3 text-right whitespace-nowrap">
                  Relawan
                </TableHead>
                <TableHead className="text-white font-extrabold text-xs py-3.5 px-3 text-right whitespace-nowrap">
                  Target relawan
                </TableHead>
                <TableHead className="text-orange-400 font-black text-xs py-3.5 px-3 text-right whitespace-nowrap">
                  Target suara 2029
                </TableHead>
                <TableHead className="text-white font-extrabold text-xs py-3.5 px-3 whitespace-nowrap">
                  Keterangan target
                </TableHead>
                <TableHead className="text-white font-extrabold text-xs py-3.5 px-3 whitespace-nowrap">
                  Skor medsos
                </TableHead>
                <TableHead className="text-white font-extrabold text-xs py-3.5 px-3 whitespace-nowrap">
                  Progres SISIR RW
                </TableHead>
                {isAdmin && (
                  <TableHead className="text-white font-extrabold text-xs py-3.5 px-2 text-center w-16">
                    Aksi
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-gray-100">
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 10 : 9} className="text-center py-12 text-gray-400 font-medium">
                    Memuat data pejabat publik...
                  </TableCell>
                </TableRow>
              ) : filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 10 : 9} className="text-center py-12 text-gray-400">
                    Tidak ada data pejabat publik ditemukan.
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((row, idx) => {
                  const medsosParts = (row.skor_medsos || '').split('·').map((s) => s.trim());
                  const medsosLevel = medsosParts[1] || '';
                  const medsosColorClass = MEDSOS_COLOR[medsosLevel] || 'text-gray-700 font-semibold';

                  return (
                    <TableRow key={row.id || idx} className="hover:bg-gray-50/70 transition-colors">
                      {/* 1. Nama & Jabatan */}
                      <TableCell className="py-3 px-4">
                        <div className="font-black text-gray-950 text-xs tracking-tight">
                          {row.nama}
                        </div>
                        <div className="text-[11px] text-gray-500 font-medium mt-0.5">
                          {row.jabatan}
                        </div>
                      </TableCell>

                      {/* 2. Dapil */}
                      <TableCell className="py-3 px-3 text-xs font-semibold text-gray-800 whitespace-nowrap">
                        {row.dapil}
                      </TableCell>

                      {/* 3. Suara Pribadi 2024 */}
                      <TableCell className="py-3 px-3 text-right text-xs font-black text-gray-950 whitespace-nowrap">
                        {row.suara_pribadi_2024 ? row.suara_pribadi_2024.toLocaleString('id-ID') : '—'}
                      </TableCell>

                      {/* 4. Relawan */}
                      <TableCell className="py-3 px-3 text-right text-xs font-semibold text-gray-800 whitespace-nowrap">
                        {row.relawan || '—'}
                      </TableCell>

                      {/* 6. Target Relawan */}
                      <TableCell className="py-3 px-3 text-right text-xs font-medium text-gray-600 whitespace-nowrap">
                        {row.target_relawan || '—'}
                      </TableCell>

                      {/* 7. Target Suara 2029 (ORANGE BOLD) */}
                      <TableCell className="py-3 px-3 text-right text-xs font-black text-orange-600 whitespace-nowrap">
                        {row.target_suara_2029 ? row.target_suara_2029.toLocaleString('id-ID') : '—'}
                      </TableCell>

                      {/* 8. Keterangan Target */}
                      <TableCell className="py-3 px-3 text-xs font-medium text-gray-700 max-w-[200px]">
                        {row.keterangan_target || '—'}
                      </TableCell>

                      {/* 9. Skor Medsos */}
                      <TableCell className="py-3 px-3 text-xs whitespace-nowrap">
                        <span className={medsosColorClass}>
                          {row.skor_medsos || '—'}
                        </span>
                      </TableCell>

                      {/* 10. Progres SISIR RW */}
                      <TableCell className="py-3 px-3 text-xs italic font-medium text-gray-400 whitespace-nowrap">
                        {row.progres_sisir_rw || '—'}
                      </TableCell>

                      {/* 11. Actions */}
                      {isAdmin && (
                        <TableCell className="py-3 px-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleOpenEdit(row)}
                              className="p-1 rounded text-gray-400 hover:text-gray-700 transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setDeleteId(row.id)}
                              className="p-1 rounded text-gray-300 hover:text-red-600 transition-colors"
                              title="Hapus"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* FOOTNOTE (EXACT COPY FROM SLIDE) */}
        <div className="p-3 bg-gray-50/70 border-t border-gray-100 text-[11px] text-gray-500 font-medium">
          <span className="font-bold">—</span> = belum dilaporkan. Suara pribadi 2024 menurut data KPU; laporan pejabat: Ramzi 9.200 dan Ai Sri 4.061. Definisi target berbeda-beda sesuai laporan masing-masing.
        </div>
      </div>

      {/* BOTTOM CALLOUT BANNER (EXACT COPY FROM PRESENTATION SLIDE) */}
      <div className="bg-[#1f2937] text-white p-5 rounded-2xl shadow-sm border border-gray-800 flex items-start gap-3">
        <span className="w-1.5 h-10 bg-orange-500 rounded-full flex-shrink-0 mt-0.5"></span>
        <div className="space-y-1">
          <p className="text-sm font-extrabold text-white">
            Tujuh anggota DPRD terpilih 2024 melaporkan 2.138 relawan.
          </p>
          <p className="text-xs text-gray-300 font-medium">
            Skor medsos dari laporan perbandingan 3 Oktober 2026; analisis lengkap media sosial dan pemberitaan di slide 10–19.
          </p>
        </div>
      </div>

      {/* MODAL FORM TAMBAH / EDIT PEJABAT */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-black text-gray-900">
              {editingItem ? 'Edit Pejabat Publik' : 'Tambah Pejabat Publik'}
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Perbarui data capaian, target suara, dan relawan anggota dewan.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-bold text-gray-700">Nama Lengkap & Gelar *</Label>
              <Input
                value={form.nama}
                onChange={(e) => setForm({ ...form, nama: e.target.value })}
                placeholder="Contoh: Hj. Leni Liawati, S.Si."
                className="mt-1 rounded-xl h-10 font-medium"
                required
              />
            </div>

            <div>
              <Label className="text-xs font-bold text-gray-700">Jabatan di Fraksi / Komisi / AKD *</Label>
              <Input
                value={form.jabatan}
                onChange={(e) => setForm({ ...form, jabatan: e.target.value })}
                placeholder="Contoh: Ketua Fraksi PKS · Anggota Komisi III"
                className="mt-1 rounded-xl h-10 font-medium"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold text-gray-700">Dapil *</Label>
                <Select
                  value={form.dapil}
                  onValueChange={(val) => setForm({ ...form, dapil: val })}
                >
                  <SelectTrigger className="mt-1 rounded-xl h-10 font-medium">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAPIL_OPTIONS.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-700">Suara Pribadi 2024</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.suara_pribadi_2024}
                  onChange={(e) => setForm({ ...form, suara_pribadi_2024: e.target.value })}
                  placeholder="5700"
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold text-gray-700">Jumlah Relawan Dilaporkan</Label>
                <Input
                  value={form.relawan}
                  onChange={(e) => setForm({ ...form, relawan: e.target.value })}
                  placeholder="311 / 350 (s.d. 2026)"
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-700">Target Relawan</Label>
                <Input
                  value={form.target_relawan}
                  onChange={(e) => setForm({ ...form, target_relawan: e.target.value })}
                  placeholder="700+ / 302 / —"
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold text-gray-700">Target Suara 2029 *</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.target_suara_2029}
                  onChange={(e) => setForm({ ...form, target_suara_2029: e.target.value })}
                  placeholder="11400"
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-700">Keterangan Target</Label>
                <Input
                  value={form.keterangan_target}
                  onChange={(e) => setForm({ ...form, keterangan_target: e.target.value })}
                  placeholder="Target 2029 / Ikhtiar 2029..."
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold text-gray-700">Skor Medsos</Label>
                <Input
                  value={form.skor_medsos}
                  onChange={(e) => setForm({ ...form, skor_medsos: e.target.value })}
                  placeholder="61,8 · Tengah / 78,6 · Atas"
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>

              <div>
                <Label className="text-xs font-bold text-gray-700">Progres SISIR RW</Label>
                <Input
                  value={form.progres_sisir_rw}
                  onChange={(e) => setForm({ ...form, progres_sisir_rw: e.target.value })}
                  placeholder="[ISI DATA] atau progress..."
                  className="mt-1 rounded-xl h-10 font-medium"
                />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">
                Batal
              </Button>
              <Button type="submit" className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold">
                {editingItem ? 'Simpan Perubahan' : 'Tambah Pejabat'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CONFIRM DELETE */}
      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(op) => !op && setDeleteId(null)}
        title="Hapus Data Pejabat Publik"
        description="Apakah Anda yakin ingin menghapus data pejabat publik ini?"
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default PejabatPublik;
